<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<template #headerBody>
			<div>
				<div class="mb-20">
					<BSelector
						design="split"
						v-model="values.loads.live.distributed.cfgMode.deltaCfgOption"
						:options="simulationOptions.liveDistributedDeltaCfg.options"
					></BSelector>
				</div>

				<div class="grid--4 mb-20">
					<BFieldBlock
						v-for="(num, index) in 3"
						:key="index"
						:label="`Ψ${index}`"
					>
						<BFieldNumber
							:disabled="
								values.loads.live.distributed.cfgMode.deltaCfgOption !==
									'customized'
							"
							v-model="values.loads.live.distributed.deltas[index]"
							:min="0"
							:precision="2"
							:step="0.01"
						/>
					</BFieldBlock>
				</div>
			</div>
		</template>

		<div class="simulator__grid  simulator__grid--1-2">
			<div>
				<div class="sheet__controlTitle mb-20">
					{{ $t("Carga distribuída") }}
				</div>
				<ImageLoadsDistributed
					class="sheet__image"
					:isSpanBySpan="values.loads.live.distributed.cfgMode.isSpanBySpan"
				/>
			</div>
			<div>
				<div class="mb-40">
					<BToggle
						v-model="values.loads.live.distributed.cfgMode.isSpanBySpan"
						:label="$t('Valor por vão')"
					></BToggle>
				</div>
			</div>
		</div>

		<div
			class="simulator__grid  simulator__grid--1-2"
			v-if="!values.loads.live.distributed.cfgMode.isSpanBySpan"
		>
			<div></div>
			<div class="grid--4 align-items-end">
				<BFieldBlock :label="$t('Número de vãos')">
					<BFieldNumber
						:step-btns="true"
						:disabled="true"
						:value="values.spans.main.length"
						:step="1"
						:precision="0"
					/>
				</BFieldBlock>

				<BFieldBlock :label="$t('Carga distribuída')">
					<BFieldNumber
						v-model="values.loads.live.distributed[sampleSpanType][0]"
						suffix="kN/m2"
						:min="0"
						:precision="2"
						:step="0.01"
					/>
				</BFieldBlock>
			</div>
		</div>

		<div class="simulator__grid  simulator__grid--1" v-else>
			<SimulatorSpanMappedValuesEditor
				:values="values.loads.live.distributed"
				:includeConsolesInBulkEdit="false"
				:fieldProps="{
					suffix: 'kN/m2',
					min: 0,
					precision: 2,
					step: 0.01,
				}"
			/>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid  simulator__grid--1-2">
			<div>
				<div class="sheet__controlTitle mb-20">{{ $t("Carga pontual") }}</div>
				<ImageLoadsPoint class="sheet__image" />
			</div>
			<div>
				<div class="mb-60">
					<div
						v-for="(item, idx) in values.loads.live.point"
						:key="idx"
						class="mb-80"
					>
						<div
							class="mb-40 flex align-items-center"
							:style="{
								gap: '20px',
							}"
						>
							<BSelector
								v-model="item.targetSpans"
								:options="spanDescriptions"
								:multiple="true"
							></BSelector>

							<BBtn
								v-if="idx !== 0"
								size="m"
								color="secondary"
								shape="circle"
								@click="handleDeleteliveOcassionalItemBtnClick(idx)"
							>
								<BIcon name="Close" size="20px" />
							</BBtn>
						</div>

						<div class="grid--4">
							<BFieldBlock label="P1">
								<BFieldNumber
									v-model="item.loads[0]"
									:min="0"
									suffix="kN"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="P2">
								<BFieldNumber
									v-model="item.loads[1]"
									:min="0"
									suffix="kN"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="L1">
								<BFieldNumber
									v-model="item.loads[2]"
									:min="0"
									:max="100"
									suffix="%"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="L2">
								<BFieldNumber
									v-model="item.loads[3]"
									:min="0"
									:max="100"
									suffix="%"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>
						</div>
					</div>
				</div>

				<button
					class="sheet__bigAddBtn"
					@click="handleAddliveOcassionalItemBtnClick"
				>
					{{ $t("Adicionar grupo") }}
					<BIcon name="Add"></BIcon>
				</button>
			</div>
		</div>

		<div class="sheet__footer">
			<BBtn size="l" variant="text" @click="handleRestartBtnClick">{{
				$t("Reiniciar")
			}}</BBtn>
			<BBtn size="l" @click="handleSaveBtnClick" :disabled="isSaving">{{
				$t("Salvar e Continuar")
			}}</BBtn>
		</div>
	</SimulatorSheet>
</template>
<script>
import { mapMutations, mapActions, mapState, mapGetters } from "vuex";
import { excludeKeys, deepClone, mapN, wait } from "@/utils";

import SimulatorSheet from "./SimulatorSheet.vue";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BToggle from "@/components/base/BToggle.vue";
import BIcon from "@/components/base/BIcon/index.vue";

import SimulatorSpanMappedValuesEditor from "./SimulatorSpanMappedValuesEditor";

import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";
import mixinObserveChanges from "@/pages/Simulator/mixins/observeChanges";
import ImageLoadsPoint from "./Images/ImageLoadsPoint.vue";
import ImageLoadsDistributed from "./Images/ImageLoadsDistributed/index.vue";

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		BIcon,
		SimulatorSpanMappedValuesEditor,
		SimulatorSheet,
		ImageLoadsPoint,
		ImageLoadsDistributed,
	},
	mixins: [mixinEmitLifecycleEvents, mixinObserveChanges],
	props: {
		...SimulatorSheet.props,
	},
	data() {
		return {
			values: null,
			isSaving: false,
			watchTriggeredChange: false,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},
		...mapGetters("simulator", ["spanDescriptions", "isFirstStepComplete"]),
	},
	methods: {
		...mapMutations("simulator", ["setSimulationValues"]),
		...mapActions("simulator", ["save"]),

		handleSaveBtnClick() {
			this.$emit("save", this.values);
		},

		handleRestartBtnClick() {
			this.$emit("restart");
		},

		handleAddliveOcassionalItemBtnClick() {
			this.values.loads.live.point.push({
				targetSpans: [],
				loads: [0, 0, 0, 0],
			});
		},

		handleDeleteliveOcassionalItemBtnClick(itemIdx) {
			this.values.loads.live.point.splice(itemIdx, 1);
		},
	},

	watch: {
		"values.loads.live.distributed": {
			handler(val) {
				if (this.watchTriggeredChange) return;

				if (!val.cfgMode.isSpanBySpan) {
					const sampleItem = val[this.sampleSpanType][0];

					val.main = val.main.map((item) => sampleItem);
					val.console = val.console.map((item) => sampleItem);

					//me so sorry if someone touches this code
					this.watchTriggeredChange = true;
					this.$nextTick(() => {
						this.watchTriggeredChange = false;
					});
				}
			},
			deep: true,
		},
	},

	created() {
		this.values = deepClone(this.storeValues);
		this.sampleSpanType = this.values.spans.useConsole ? "console" : "main";
	},
};
</script>

<style lang="scss">
.spansTable__smallText {
	font-size: var(--font-size-s);
	color: var(--color-neutral-500);
}

.spansTable__checkboxCell {
	display: flex;
	align-items: center;
	gap: 20px;
}

.spansTable__headCell2 {
	padding-left: 16px;
}

.spansTable__spanNumber {
	font-variant-numeric: tabular-nums;
}
</style>
